/**
 * Format client name in the standard format:
 * "LastName1, FirstName1 & FirstName2 LastName2"
 *
 * If spouse has same last name: "LastName, FirstName1 & FirstName2"
 * If no spouse: "LastName, FirstName"
 */
export const formatClientName = (
  firstName1: string,
  lastName1: string,
  firstName2?: string,
  lastName2?: string
): string => {
  if (!firstName1 || !lastName1) {
    return 'Unknown Client';
  }

  // No spouse
  if (!firstName2) {
    return `${lastName1}, ${firstName1}`;
  }

  // Spouse with same last name or no spouse last name provided
  if (!lastName2 || lastName2.toLowerCase() === lastName1.toLowerCase()) {
    return `${lastName1}, ${firstName1} & ${firstName2}`;
  }

  // Spouse with different last name
  return `${lastName1}, ${firstName1} & ${firstName2} ${lastName2}`;
};

/**
 * Extract client name from form data
 */
export const getClientNameFromFormData = (formData: any): string => {
  const personal = formData?.personal;

  if (!personal) {
    return 'Unknown Client';
  }

  return formatClientName(
    personal.client1?.firstName,
    personal.client1?.lastName,
    personal.client2?.firstName,
    personal.client2?.lastName
  );
};

/**
 * Normalize client name for grouping (trim and collapse spaces)
 */
export const normalizeClientName = (name: string): string => {
  return name.trim().replace(/\s+/g, ' ');
};

/**
 * Generate a unique client group key combining names and dates of birth
 * This ensures clients with the same name but different DOBs are treated separately
 */
export const generateClientGroupKey = (
  client1FirstName?: string,
  client1LastName?: string,
  client1DateOfBirth?: string,
  client2FirstName?: string,
  client2LastName?: string,
  client2DateOfBirth?: string,
  fallbackId?: string
): string => {
  // Create normalized name key
  const name1 = client1FirstName && client1LastName
    ? `${client1LastName.toLowerCase()}-${client1FirstName.toLowerCase()}`
    : 'unknown';

  const name2 = client2FirstName && client2LastName
    ? `${client2LastName.toLowerCase()}-${client2FirstName.toLowerCase()}`
    : '';

  // If no DOB provided, use fallback ID to ensure uniqueness
  if (!client1DateOfBirth && !client2DateOfBirth) {
    return `${name1}${name2 ? '__' + name2 : ''}__no-dob__${fallbackId || 'unknown'}`;
  }

  // Create key with names and both DOBs
  const dob1 = client1DateOfBirth || 'no-dob1';
  const dob2 = client2DateOfBirth || 'no-dob2';

  return `${name1}${name2 ? '__' + name2 : ''}__${dob1}__${dob2}`;
};

/**
 * Format client display name to show both partners
 * Examples:
 *   - "John Smith" (no partner)
 *   - "John Smith and Jane Doe" (different last names)
 *   - "John and Jane Smith" (same last name)
 */
export const formatClientDisplayName = (
  firstName1: string,
  lastName1: string,
  firstName2?: string,
  lastName2?: string
): string => {
  if (!firstName1 || !lastName1) {
    return 'Unknown Client';
  }

  // No partner
  if (!firstName2) {
    return `${firstName1} ${lastName1}`;
  }

  // Partner with same last name or no partner last name provided
  if (!lastName2 || lastName2.toLowerCase() === lastName1.toLowerCase()) {
    return `${firstName1} and ${firstName2} ${lastName1}`;
  }

  // Partner with different last name
  return `${firstName1} ${lastName1} and ${firstName2} ${lastName2}`;
};
